import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  CorporationDto,
  CorporationSearchDto,
  CorporateService
} from '@umss/shared/data-access';
import {
  SelectLoading,
  SelectOption,
  UmssSelectComponent
} from '../select/select.component';
import { UmssVerticalFieldComponent } from '../vertical-field/vertical-field.component';

const PAGE_SIZE = 20;

/** Searchable, paginated corporation picker (ControlValueAccessor), mirroring `umss-facility-select`. */
@Component({
  selector: 'umss-corporation-select',
  standalone: true,
  imports: [CommonModule, UmssSelectComponent, UmssVerticalFieldComponent],
  template: `
    <umss-vertical-field
      [label]="label"
      [required]="required"
      [disabled]="disabled"
    >
      <umss-select
        [placeholder]="placeholder"
        [options]="options"
        [value]="value"
        [searchable]="searchable"
        [clearable]="clearable"
        [multiple]="multiple"
        [disabled]="disabled"
        [required]="required"
        [loading]="loading"
        [isPaginated]="true"
        (valueChange)="onValueChange($event)"
        (searchChange)="onSearchChange($event)"
        (onScroll)="onScrollOptions($event)"
      />
    </umss-vertical-field>
  `,
  styles: `
    :host {
      display: contents;
    }

    ::ng-deep .vertical-field {
      flex: 1;
      min-width: 0;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssCorporationSelectComponent),
      multi: true
    }
  ]
})
export class UmssCorporationSelectComponent
  implements ControlValueAccessor, OnInit
{
  private readonly corporateService = inject(CorporateService);

  @Input() placeholder = 'Select corporation';
  @Input() searchable = true;
  @Input() clearable = true;
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() label = '';
  @Input() required = false;
  @Input() payload: Partial<CorporationSearchDto> = {};

  options: SelectOption[] = [];
  loading: SelectLoading = { selector: false, options: false };
  value: string | string[] | null = null;

  private currentPage = 1;
  private hasNextPage = false;
  private lastSearchTerm: string | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string | string[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.loadCorporations(1, null, false);
  }

  writeValue(value: string | string[] | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: string | string[] | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  onSearchChange(searchTerm: string | null): void {
    this.lastSearchTerm = searchTerm;
    this.loadCorporations(1, searchTerm, false);
  }

  onScrollOptions(percent: number): void {
    if (percent < 80 || !this.hasNextPage || this.loading.options) return;
    this.loadCorporations(this.currentPage + 1, this.lastSearchTerm, true);
  }

  private loadCorporations(
    page: number,
    searchTerm: string | null,
    append: boolean
  ): void {
    this.loading = { selector: !append, options: true };

    this.corporateService
      .getAll({
        sortBy: 'name',
        sortDescending: false,
        ...this.payload,
        searchTerm: searchTerm ?? undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE
      })
      .subscribe({
        next: (response) => {
          const newOptions = this.mapToOptions(response.items);

          this.options = append ? [...this.options, ...newOptions] : newOptions;

          this.currentPage = page;
          this.hasNextPage = response.hasNextPage;
          this.loading = { selector: false, options: false };
        },
        error: () => {
          if (!append) this.options = [];
          this.loading = { selector: false, options: false };
        }
      });
  }

  private mapToOptions(corporations: CorporationDto[]): SelectOption[] {
    return corporations.map((corporation) => ({
      value: corporation.id,
      label: corporation.name,
      description: this.describe(corporation)
    }));
  }

  private describe(corporation: CorporationDto): string | undefined {
    const location = [corporation.city, corporation.state]
      .filter(Boolean)
      .join(', ');

    return location || corporation.email || undefined;
  }
}
